import { createSelector } from 'reselect';
import { paramsToQueryString } from 'Helpers/url';
import { pickBy } from 'Helpers/objects';
import { getSeoConfigParams, configToObject, getSeoFooterURL } from 'Helpers/seo';
import {
    parseParams as parsePopularSearchesParams
} from 'Helpers/seoPopularSearches';
import { urls as footerUrls, TYPE } from 'Reducers/seoFooterLinks';
import { urls as popularSearchesUrls } from 'Reducers/seoPopularSearches';
import { categorySelector } from 'Selectors/categories';
import { locationByIdSelector } from 'Selectors/location';
import { buildURL } from 'Helpers/url';
import { getSEOLang } from 'Selectors/config';
import { getType } from 'Reducers/helpers/typeModifier';

const getFooterFromState = createSelector(
    state => state.seoFooterLinks,
    (_, url, params) => `${url}?${paramsToQueryString(params)}`,
    (seoFooterLinks, url) => {
        const reducerUrl = getType(TYPE, url);

        return {
            seoFooterLinks,
            reducerUrl
        };
    }
);

export const getIsError = createSelector(
    getFooterFromState,
    ({ seoFooterLinks, reducerUrl }) => {
        return seoFooterLinks && seoFooterLinks.isError && seoFooterLinks.isError[reducerUrl];
    }
);

export const getIsFetching = createSelector(
    getFooterFromState,
    ({ seoFooterLinks, reducerUrl }) => {
        return seoFooterLinks && seoFooterLinks.isFetching && seoFooterLinks.isFetching[reducerUrl];
    }
);

export const getErrorsMetadata = createSelector(
    getFooterFromState,
    ({ seoFooterLinks, reducerUrl }) => {
        return seoFooterLinks && seoFooterLinks.errorsMetadata && seoFooterLinks.errorsMetadata[reducerUrl];
    }
);

export const getSections = createSelector(
    (state, _, { key = 'elements' }) => state.seoFooterLinks[key],
    (_, url, { key, ...params }) => `${url}?${paramsToQueryString(params)}`, // eslint-disable-line no-unused-vars
    (footerLinks, key) => footerLinks[key]
);

export const getFooterSections = createSelector(
    (state, { config }) => {
        const lang = getSEOLang(state);
        const url = getSeoFooterURL(config, footerUrls);

        return getSections(state, url, getSeoConfigParams(config, lang));
    },
    (_, { sections }) => sections,
    ({ sections: baseSections = [] } = {}, sections = []) => {
        if (!sections.length) {
            return baseSections;
        }

        const usedKeys = {};
        const mergedSections = baseSections.reduce((out, section) => {
            const found = sections.find(s => s.id === section.id);

            if (found) {
                out.push(found);
                usedKeys[found.id] = true;
            }
            else {
                out.push(section);
            }

            return out;
        }, []);

        return sections.reduce((out, section) => {
            if (!usedKeys[section.id]) {
                out.push(section);
            }

            return out;
        }, mergedSections);
    }
);

export const getSearches = (
    state,
    { key = 'elements', ...params }
) => state.seoPopularSearches[key][`${popularSearchesUrls.getPopularSearches()}?${paramsToQueryString(pickBy(params))}`];

export const getPopularSearches = (
    state,
    { config, params }
) => {
    if (params.text) {
        return [];
    }
    const lang = getSEOLang(state);
    const settings = configToObject(config);
    const reqParams = {
        ...getSeoConfigParams(config, lang),
        ...parsePopularSearchesParams(settings, params)
        // Filters are not used by mystique to send popular searches
        // ...parsePopularSearchesQuery(location.query)
    };

    const searches = (getSearches(state, reqParams) || {}).searches || [];
    const category = categorySelector(state, params.categoryID || params.category);
    const locationObject = locationByIdSelector(state)(params.geoID);

    // This should be removed once this fixed from BE, https://sobek.atlassian.net/browse/OLXIN-5449
    const newSearches = searches.map(search => {
        const popularSearchTerm = config.get('SEO', 'popularRedirectTerms')?.find(popularSearchTerm => popularSearchTerm.term === search.term);

        if (popularSearchTerm) {
            return { ...search, ...popularSearchTerm };
        }
        return search;
    });

    return newSearches.map(popularSearch => ({
        ...popularSearch,
        href: buildURL({
            category,
            location: locationObject,
            search: config.get('SEO', 'popularRedirectTerms')?.some(popularRedirectTerm => popularRedirectTerm.term === popularSearch.term) ? null : popularSearch.term,
            params: {
                filter: popularSearch.filters
            }
        }, {
            useFakeLevelSlug: config.get('SEO', 'useFakeLevelSlug'),
            categoryCover: config.get('categoryCover')
        })
    }));
};
